import "./Finance.scss";
import { NavLink, Outlet, useLocation, Navigate } from "react-router-dom";

import { Typography } from "../../ui";
import { DOMAIN_CONFIG } from "../../configs/main";
import useUser from "../../data/useUser";
import useAdminUser from "../../data/useAdminUser";
import { useEffect, useMemo, useState } from "react";
import { DOMAIN_API_HOST } from "../../configs/main";
import { isClickruIndividual } from "../../utils/helpers";

const RETARGEO_NOTICE_UNTIL = new Date("2026-04-01");

const MONTHS_RU = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

const parseFilenameFromContentDisposition = (value) => {
  if (!value) return null;
  const matchStar = value.match(/filename\*\s*=\s*([^;]+)/i);
  if (matchStar?.[1]) {
    const raw = matchStar[1].trim();
    const cleaned = raw.replace(/^utf-8''/i, "").replace(/^"|"$/g, "");
    try {
      return decodeURIComponent(cleaned);
    } catch {
      return cleaned;
    }
  }

  const match = value.match(/filename\s*=\s*([^;]+)/i);
  if (match?.[1]) {
    return match[1].trim().replace(/^"|"$/g, "");
  }

  return null;
};

const getPeriodLabelFromNumber = (numberStr) => {
  const m = String(numberStr || "").match(/^[^-]+-(\d{4})-(\d{2})-/);
  if (!m) return "";
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!year || !month || month < 1 || month > 12) return "";
  const monthName = MONTHS_RU[month - 1];
  if (!monthName) return "";
  return `${monthName[0].toUpperCase()}${monthName.slice(1)} ${year}`;
};

function Finance() {
  const { pathname } = useLocation();
  const { user } = useUser();
  const personal_account = user?.user_balance?.personal_account;
  const hideReplenishment = isClickruIndividual(user);
  const hideFinance = user?.agency === "click_ru_753";
  const { user: adminUser, isLoading: adminUserLoading, token: adminToken } = useAdminUser();
  const isSuperAdmin = ["SUPERADMIN", "SUPERUSER"].includes(adminUser?.role);
  const isSuperuser = adminUser?.role === "SUPERUSER";
  const closingDocsTabResolved = !adminToken || !adminUserLoading;

  const [closingReports, setClosingReports] = useState([]);
  const [closingReportsLoading, setClosingReportsLoading] = useState(false);
  const [closingReportsError, setClosingReportsError] = useState("");
  const [downloadingKey, setDownloadingKey] = useState(null);

  const showRetargeoNotice =
    DOMAIN_CONFIG.brand === "retargeo" && new Date() < RETARGEO_NOTICE_UNTIL;
  const isClosingDocumentsTab = pathname === "/finance/closing-documents";
  const formattedPersonalAccount = String(personal_account ?? "").padStart(6, "0");

  useEffect(() => {
    if (!isClosingDocumentsTab) return;

    let cancelled = false;
    const load = async () => {
      try {
        setClosingReportsLoading(true);
        setClosingReportsError("");

        const token = localStorage.getItem("token");
        const res = await fetch(`${DOMAIN_API_HOST}/api/v1/payment/closing-reports/`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const message = err?.detail || "Не удалось получить закрывающие документы";
          throw new Error(message);
        }

        const data = await res.json().catch(() => []);
        if (cancelled) return;
        setClosingReports(Array.isArray(data) ? data : []);
      } catch (e) {
        if (cancelled) return;
        setClosingReports([]);
        setClosingReportsError(e?.message || "Не удалось получить закрывающие документы");
      } finally {
        if (!cancelled) setClosingReportsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isClosingDocumentsTab]);

  const closingDocuments = useMemo(() => {
    const groups = new Map();
    for (const r of closingReports) {
      const period = getPeriodLabelFromNumber(r?.number) || "—";
      if (!groups.has(period)) groups.set(period, []);
      groups.get(period).push(r);
    }

    return Array.from(groups.entries()).map(([period, documents]) => ({
      period,
      documents,
    }));
  }, [closingReports]);

  const handleDownloadClosingReport = async (report, { asHtml = false } = {}) => {
    const reportId = report?.id;
    if (!reportId) return;

    const link = document.createElement("a");
    const dlKey = `${reportId}:${asHtml ? "html" : "pdf"}`;
    try {
      const userToken = localStorage.getItem("token");
      const adminToken = localStorage.getItem("admin_token");
      const token = asHtml ? adminToken : userToken;
      if (!token) {
        setClosingReportsError(
          asHtml
            ? "Нет токена администратора для скачивания HTML"
            : "Не удалось скачать документ"
        );
        return;
      }
      setDownloadingKey(dlKey);
      const qs = asHtml ? "?as_html=true" : "";
      const res = await fetch(
        `${DOMAIN_API_HOST}/api/v1/payment/closing-reports/${reportId}/download${qs}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const message = err?.detail || "Не удалось скачать документ";
        throw new Error(message);
      }

      const blob = await res.blob();
      const cd = res.headers?.get("content-disposition");
      const ext = asHtml ? "html" : "pdf";
      const filename =
        parseFilenameFromContentDisposition(cd) ||
        `${String(report?.number || "closing-report")}.${ext}`;

      link.download = filename;
      link.href = window.URL.createObjectURL(blob);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      setClosingReportsError(e?.message || "Не удалось скачать документ");
    } finally {
      setDownloadingKey(null);
    }
  };

  const defaultFinanceTab = hideReplenishment ? "/finance/balance-history" : "/finance/replenishment";

  if (pathname === "/finance") {
    return <Navigate to={defaultFinanceTab} />;
  }

  if (hideFinance) {
    return <Navigate to="/" replace />;
  }

  if (hideReplenishment && pathname === "/finance/replenishment") {
    return <Navigate to="/finance/balance-history" replace />;
  }

  if (isClosingDocumentsTab && closingDocsTabResolved && !isSuperuser) {
    return <Navigate to={defaultFinanceTab} replace />;
  }

  return (
      <div className="Finance">
        <Typography variant="h1" color="dark-gray">
          Финансы
        </Typography>
        {showRetargeoNotice && (
          <div className="retargeo-notice">
            🔔 Уважаемый клиент, сообщаем Вам, что с «01» марта 2026 года обслуживание нашего сайта{" "}
            <a href="https://retargeo.ru/" target="_blank" rel="noreferrer">
              https://retargeo.ru/
            </a>{" "}
            будет осуществляться новой компанией: ООО «Программатик групп»
          </div>
        )}
        <div className="nav">
          {!hideReplenishment && <NavLink to="/finance/replenishment">Пополнение</NavLink>}
          <NavLink to="/finance/balance-history">История баланса</NavLink>
          {closingDocsTabResolved && isSuperuser && (
            <NavLink to="/finance/closing-documents">Закрывающие документы</NavLink>
          )}
        </div>
        <div className="content">
          {isClosingDocumentsTab ? (
            <div className="FinanceClosingDocuments">
              <div className="FinanceClosingDocuments-account">
                Лицевой счет: {formattedPersonalAccount}
              </div>

              {closingReportsLoading ? (
                <Typography variant="body1" color="dark-gray">
                  Загружаем закрывающие документы…
                </Typography>
              ) : closingReportsError ? (
                <Typography variant="body1" color="dark-gray">
                  {closingReportsError}
                </Typography>
              ) : closingDocuments.length > 0 ? (
                <div className="FinanceClosingDocuments-tableWrap">
                  <table className="FinanceClosingDocuments-table">
                    <thead>
                      <tr>
                        <th>Период</th>
                        <th>Номер документа</th>
                        <th>Дата формирования документа</th>
                        <th>Итоговая сумма (с НДС)</th>
                        <th>Скачать</th>
                        {isSuperAdmin && <th>Скачать HTML</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {closingDocuments.map((item) =>
                        item.documents.map((document, index) => (
                          <tr key={document.id || document.number}>
                            {index === 0 && <td rowSpan={item.documents.length}>{item.period}</td>}
                            <td>{document.number}</td>
                            <td>{document.createdAt}</td>
                            <td>{document.amountVat}</td>
                            <td>
                              <button
                                type="button"
                                onClick={() => handleDownloadClosingReport(document)}
                                disabled={
                                  !document?.id || downloadingKey === `${document?.id}:pdf`
                                }
                              >
                                {downloadingKey === `${document?.id}:pdf`
                                  ? "Скачиваем…"
                                  : "Скачать PDF"}
                              </button>
                            </td>
                            {isSuperAdmin && (
                              <td>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDownloadClosingReport(document, { asHtml: true })
                                  }
                                  disabled={
                                    !document?.id || downloadingKey === `${document?.id}:html`
                                  }
                                >
                                  {downloadingKey === `${document?.id}:html`
                                    ? "Скачиваем…"
                                    : "Скачать HTML"}
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Typography variant="body1" color="dark-gray">
                  Закрывающих документов пока нет. Они создаются в начале месяца, если были
                  пополнения.
                </Typography>
              )}
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
  );
}

export default Finance;
